/**
 * Office alerts for required load items a driver skipped, then continued.
 * An alert stays up until that item is actually on the load.
 */

const DOCUMENT_OVERRIDE = "missing_required_document_override";
const HAZMAT_WARNING = "missing_hazmat_fields_warning";

const HAZMAT_FIELDS = [
  "unNumber",
  "properShippingName",
  "hazardClass",
  "packingGroup",
  "emergencyContact",
  "emergencyPhone",
];

const HAZMAT_LABELS = {
  isHazmat: "hazmat flag",
  unNumber: "UN number",
  properShippingName: "proper shipping name",
  hazardClass: "hazard class",
  packingGroup: "packing group",
  emergencyContact: "emergency contact",
  emergencyPhone: "emergency phone",
};

const DOC_COPY = {
  bol: {
    document: {
      title: "Bill of lading still needed",
      message: "This load moved ahead without the bill of lading.",
    },
    signature: {
      title: "Bill of lading signature still needed",
      message: "This load moved ahead without a bill of lading signature.",
    },
  },
  pod: {
    document: {
      title: "Proof of delivery still needed",
      message: "This load moved ahead without the proof of delivery.",
    },
    signature: {
      title: "Proof of delivery signature still needed",
      message: "This load moved ahead without a proof of delivery signature.",
    },
  },
};

/**
 * @param {object} [configs]
 * @returns {"upload_only"|"signature_required"}
 */
export function evidenceModeFromConfig(configs) {
  const mode = configs?.tasks?.bolPodEnforcement?.evidenceMode;
  return mode === "signature_required" ? "signature_required" : "upload_only";
}

/**
 * @param {object} [leg]
 * @returns {boolean}
 */
export function legHasRequirementException(leg) {
  const exceptions = Array.isArray(leg?.statusExceptions) ? leg.statusExceptions : [];
  return exceptions.some(
    (item) => item?.code === DOCUMENT_OVERRIDE || item?.code === HAZMAT_WARNING,
  );
}

/**
 * @param {string[]} labels
 * @returns {string}
 */
function joinLabels(labels) {
  if (labels.length <= 1) return labels[0] || "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

/**
 * @param {object} [mediaItem]
 * @returns {boolean}
 */
function hasDocument(mediaItem) {
  return Boolean(mediaItem && (mediaItem._id || mediaItem.id));
}

/**
 * @param {object} [mediaItem]
 * @returns {boolean}
 */
function hasSignature(mediaItem) {
  const evidence = mediaItem?.evidence || {};
  return (
    evidence.kind === "signature" &&
    evidence.consentAccepted === true &&
    Boolean(evidence.payloadHash)
  );
}

/**
 * Accepts the leg media object `{ bol, pod }` or a list of files with `type`.
 * @param {object|Array|null|undefined} media
 * @param {"bol"|"pod"} docType
 * @returns {object|null}
 */
function mediaRecord(media, docType) {
  if (!media) return null;
  if (Array.isArray(media)) {
    const item = media.find(
      (row) => String(row?.type || "").toLowerCase() === docType,
    );
    return hasDocument(item) ? item : null;
  }
  const item = media[docType];
  return hasDocument(item) ? item : null;
}

/**
 * @param {object} [leg]
 * @returns {string[]}
 */
function missingHazmatFields(leg) {
  const shipping = leg?.shipping || {};
  const profile = String(leg?.complianceProfile || "").toLowerCase();
  const hazmatLoad = shipping.isHazmat === true || profile === "hazmat";
  if (!hazmatLoad) return [];

  const hazmat = shipping.hazmat || {};
  const missing = [];
  if (shipping.isHazmat !== true) missing.push("isHazmat");

  HAZMAT_FIELDS.forEach((field) => {
    const value = hazmat[field];
    if (value === undefined || value === null || String(value).trim() === "") {
      missing.push(field);
    }
  });

  return missing;
}

/**
 * @param {object[]} exceptions
 * @param {string} docType
 * @returns {string}
 */
function latestReason(exceptions, docType) {
  const matches = exceptions.filter(
    (item) =>
      item?.code === DOCUMENT_OVERRIDE &&
      String(item.docType || "").toLowerCase() === docType,
  );
  const reason = String(matches[matches.length - 1]?.reason || "").trim();
  if (!reason || reason === "No reason provided") return "";
  return reason;
}

/**
 * @param {object} params
 * @param {object} [params.leg]
 * @param {object|Array} [params.media]
 * @param {string} [params.evidenceMode]
 * @param {boolean} [params.mediaReady]
 * @returns {{ id: string, title: string, message: string }[]}
 */
export function buildLoadRequirementAlerts({
  leg,
  media,
  evidenceMode = "upload_only",
  mediaReady = true,
} = {}) {
  const exceptions = Array.isArray(leg?.statusExceptions) ? leg.statusExceptions : [];
  const alerts = [];
  const signatureRequired = evidenceMode === "signature_required";

  if (mediaReady) {
    const skipped = [];
    exceptions.forEach((item) => {
      if (item?.code !== DOCUMENT_OVERRIDE) return;
      const docType = String(item.docType || "").toLowerCase();
      if ((docType === "bol" || docType === "pod") && !skipped.includes(docType)) {
        skipped.push(docType);
      }
    });

    skipped.forEach((docType) => {
      const record = mediaRecord(media, docType);
      const satisfied = signatureRequired ? hasSignature(record) : Boolean(record);
      if (satisfied) return;

      const kind = signatureRequired ? "signature" : "document";
      const copy = DOC_COPY[docType][kind];
      const reason = latestReason(exceptions, docType);
      alerts.push({
        id: `${docType}-${kind}`,
        title: copy.title,
        message: reason ? `${copy.message} Reason: ${reason}` : copy.message,
      });
    });
  }

  const hazmatSkipped = exceptions.some((item) => item?.code === HAZMAT_WARNING);
  if (hazmatSkipped) {
    const missing = missingHazmatFields(leg);
    if (missing.length) {
      const labels = missing.map((field) => HAZMAT_LABELS[field] || field);
      alerts.push({
        id: "hazmat",
        title: "Hazmat details still needed",
        message: `This load moved ahead without the ${joinLabels(labels)}.`,
      });
    }
  }

  return alerts;
}
